import { EScreenType } from "@kuika/kuika-cl-model";
import {
  createBlankDesignerScreen,
  createMasterDesignerScreen,
  reloadDesignerScreen
} from "../../designer-screen-actions";
import { getSdInstance } from "../helpers";
import type { ToolHandlerGroup } from "../types";

export const screenManagementTools: ToolHandlerGroup = {
  async: {
    ui_common_reload_screen: async () => {
      const result = await reloadDesignerScreen(getSdInstance());
      if ("error" in result) return result;

      return {
        success: true,
        message: result.message,
        ...(result.ready
          ? {}
          : {
              warning:
                "Reload triggered but the designer may not have fully loaded the screen yet. Subsequent tool calls might see stale state."
            })
      };
    },

    ui_project_create_screen: async (input) => {
      const { screenName } = input;
      if (!screenName) return { error: "screenName is required" };

      const result = await createBlankDesignerScreen(getSdInstance(), screenName);
      if ("error" in result) return result;

      // NOTE: we used to return a prose `reminder` field here ("You are
      // now on a NEW blank screen..."). That was ~40 tokens per response
      // on every create call — the same advice served on every turn of
      // every build. It now lives ONCE in the ui_screen tool description
      // (cat-screen.ts, under the `create` operation docs) so it costs
      // zero tokens per response while still reaching the LLM. The
      // `warning` field below remains because it only fires on the
      // actual stale-state condition.
      return {
        success: true,
        screenId: result.screenId,
        screenName,
        message: `Created screen "${screenName}" and navigated to it`,
        ...(result.ready
          ? {}
          : {
              warning:
                "Screen was created but the designer may not have fully loaded it yet. Subsequent tool calls might see stale state."
            })
      };
    },

    ui_project_create_master_screen: async (input) => {
      const { screenName, layout } = input;
      if (!screenName) return { error: "screenName is required" };

      const result = await createMasterDesignerScreen(getSdInstance(), screenName, layout);
      if ("error" in result) return result;

      return {
        success: true,
        screenId: result.screenId,
        screenName,
        screenType: "Master",
        // Structured readiness signal (KOA-17777): false when the designer's load
        // poll timed out — the canvas may not be on the new master and layout
        // positions were NOT applied. master-shell navigates+verifies before
        // rendering rather than trusting create_master's auto-navigate.
        ready: result.ready,
        // KOA-17797 — true when an existing master of this name was reused
        // (idempotent) rather than freshly created. Reuse it in place; do NOT
        // create an alternate-named master.
        ...(result.reused ? { reused: true } : {}),
        message: result.reused
          ? `Master screen "${screenName}" already existed — reused it and navigated to it`
          : `Created master screen "${screenName}" and navigated to it`,
        ...(layout
          ? {
              layout: {
                header: layout.header ?? 0,
                footer: layout.footer ?? 0,
                left: layout.left ?? 0,
                right: layout.right ?? 0
              }
            }
          : {}),
        ...(result.ready
          ? {}
          : {
              warning:
                "Master screen was created but the designer may not have fully loaded it yet. Subsequent tool calls might see stale state."
            })
      };
    },

    ui_screen_set_master_screen: async (input) => {
      const { masterScreenId } = input;
      const sd = getSdInstance();

      if (masterScreenId) {
        const screenList = sd.screenProvider?.state?.screenList || [];
        // The CHILD is always the current canvas screen (setMasterScreen mutates
        // state.sdId). Guard against binding a screen to itself, and against
        // giving a master screen a master — both corrupt the tree. This is the
        // exact failure when the canvas sits on the master and set_master_screen
        // is called with that master's id (screen bound to itself).
        const currentScreenId = sd.screenProvider?.state?.sdId;
        if (currentScreenId && String(currentScreenId) === String(masterScreenId)) {
          return {
            error: `Cannot assign a screen as its own master. The current canvas screen and the master (${masterScreenId}) are the same screen — navigate to the CHILD content screen first, then bind it to the master.`
          };
        }
        const currentScreen = screenList.find((s) => String(s.id) === String(currentScreenId));
        if (currentScreen?.publicMetaData?.screenType === EScreenType.Master) {
          return {
            error: `The current screen "${currentScreen.name}" is itself a master screen; a master cannot be bound to another master. Navigate to a content screen first, then assign the master.`
          };
        }
        // Verify master screen exists and is actually a master screen
        const masterScreen = screenList.find((s) => String(s.id) === String(masterScreenId));
        if (!masterScreen) return { error: `Screen "${masterScreenId}" not found` };
        if (masterScreen.publicMetaData?.screenType !== EScreenType.Master) {
          return {
            error: `Screen "${masterScreen.name}" is not a master screen (type: ${masterScreen.publicMetaData?.screenType})`
          };
        }
      }

      sd.screenProvider.setMasterScreen(masterScreenId || undefined);

      return {
        success: true,
        masterScreenId: masterScreenId || null,
        message: masterScreenId ? `Master screen assigned: ${masterScreenId}` : "Master screen unassigned"
      };
    }
  }
};
